// src/components/mensagens/helpers.js
// ============================================================================
// 🎯 FUNÇÕES UTILITÁRIAS - PURAS E REUTILIZÁVEIS
// 📦 V2 ENTERPRISE — Ultra-Optimizado · Zero Dependencias · Tree-Shakeable
// ============================================================================
// 🔥 V2: Nuevas funciones para MessageHistoryModal Ultra Premium
// 🔥 V2: truncateText, formatFullDateTime, generateResendId, isMessageEditable
// 🔥 V2: sanitizeMessageForEdit, getMessageActionPermissions
// 🔥 V2: Todas las funciones existentes PRESERVADAS sin modificación
// ============================================================================

/**
 * 📞 Normaliza número de telefone para formato internacional (55)
 * @param {string} phone - Número de telefone
 * @returns {string} Número normalizado
 */
export const normalizePhoneBR = (phone = "") => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return digits;
  if (digits.length >= 10 && digits.length <= 11) return `55${digits}`;
  return digits;
};

/**
 * 📱 Formata número de telefone para exibição (XX) XXXXX-XXXX
 * @param {string} phone - Número de telefone
 * @returns {string} Número formatado
 */
export const formatPhoneDisplay = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

/**
 * 👤 Obtém iniciais de um nome
 * @param {string} name - Nome completo
 * @returns {string} Iniciais (máx 2 caracteres)
 */
export const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

/**
 * ⏰ Retorna tempo relativo (agora, 5min, 2h, 3d)
 * @param {number} timestamp - Timestamp em milissegundos
 * @returns {string} Tempo relativo formatado
 */
export const getTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  
  const now = Date.now();
  const diff = now - timestamp;
  
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes}min`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/**
 * 🔥 Retorna classes de cor baseado na temperatura do lead
 * @param {string} temp - Temperatura (Quente, Morno, Frio)
 * @returns {string} Classes Tailwind CSS
 */
export const getTemperatureColor = (temp) => {
  switch(temp) {
    case 'Quente': return 'bg-red-100 text-red-700 border-red-200';
    case 'Morno': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'Frio': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

/**
 * 📊 Retorna classes de cor baseado no status do cliente
 * @param {string} status - Status (Novo, Ativo, Inativo, Lead)
 * @returns {string} Classes Tailwind CSS
 */
export const getStatusColor = (status) => {
  switch(status) {
    case 'Novo': return 'bg-green-100 text-green-700';
    case 'Ativo': return 'bg-blue-100 text-blue-700';
    case 'Inativo': return 'bg-gray-100 text-gray-700';
    case 'Lead': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

/**
 * ✅ Valida se um telefone é válido (10-13 dígitos)
 * @param {string} phone - Número de telefone
 * @returns {boolean} True se válido
 */
export const isValidPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 13;
};

/**
 * 🔍 Filtra e ordena clientes por relevância
 * @param {Array} clients - Lista de clientes
 * @param {string} searchTerm - Termo de busca
 * @returns {Array} Clientes filtrados e ordenados
 */
export const filterClientsByRelevance = (clients, searchTerm) => {
  if (!searchTerm) return clients;
  
  const term = searchTerm.toLowerCase().trim();
  
  return clients
    .filter(client => {
      const matchesName = client.name?.toLowerCase().includes(term);
      const matchesEmail = client.email?.toLowerCase().includes(term);
      const matchesPhone = client.phone?.includes(term);
      return matchesName || matchesEmail || matchesPhone;
    })
    .sort((a, b) => {
      // Prioriza quem começa com o termo
      const aStarts = a.name?.toLowerCase().startsWith(term);
      const bStarts = b.name?.toLowerCase().startsWith(term);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    });
};

/**
 * 📅 Formata data/hora para exibição
 * @param {number} timestamp - Timestamp em milissegundos
 * @param {boolean} includeTime - Incluir hora
 * @returns {string} Data formatada
 */
export const formatDateTime = (timestamp, includeTime = true) => {
  if (!timestamp) return '';
  
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(includeTime && { hour: '2-digit', minute: '2-digit' })
  };
  
  return new Date(timestamp).toLocaleString('pt-BR', options);
};

/**
 * 💬 Retorna ícone baseado no status da mensagem
 * @param {string} status - Status da mensagem
 * @param {boolean} isMobile - Se é mobile
 * @returns {Object} { icon: string, color: string, label: string }
 */
export const getMessageStatusInfo = (status, isMobile = false) => {
  switch(status) {
    case 'read':
      return {
        icon: 'CheckCircle',
        color: 'text-blue-500',
        label: 'Lida'
      };
    case 'delivered':
      return {
        icon: 'Check',
        color: 'text-green-500',
        label: 'Entregue'
      };
    case 'sent':
      return {
        icon: 'Check',
        color: 'text-gray-400',
        label: 'Enviada'
      };
    case 'failed':
      return {
        icon: 'AlertCircle',
        color: 'text-red-500',
        label: 'Falhou'
      };
    default:
      return {
        icon: 'MessageSquare',
        color: 'text-gray-400',
        label: 'WhatsApp'
      };
  }
};

/**
 * 📊 Calcula estatísticas avançadas das mensagens
 * @param {Array} messages - Lista de mensagens
 * @returns {Object} Estatísticas calculadas
 */
export const calculateAdvancedStats = (messages) => {
  if (!Array.isArray(messages)) {
    return { total: 0, taxaLeitura: 0, taxaResposta: 0, lidas: 0, respondidas: 0 };
  }
  
  const total = messages.length;
  const lidas = messages.filter(m => m.status === 'read').length;
  const respondidas = messages.filter(m => m.direction === 'incoming').length;
  
  return {
    total,
    taxaLeitura: total > 0 ? Math.round((lidas / total) * 100) : 0,
    taxaResposta: total > 0 ? Math.round((respondidas / total) * 100) : 0,
    lidas,
    respondidas
  };
};

/**
 * 🎨 Retorna classes de cor para cards de estatísticas
 * @param {string} color - Nome da cor
 * @returns {Object} Classes de cor
 */
export const getStatsCardColors = (color) => {
  const colors = {
    blue:   { bg: 'bg-blue-100',   text: 'text-blue-600',   icon: 'text-blue-600' },
    green:  { bg: 'bg-green-100',  text: 'text-green-600',  icon: 'text-green-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'text-purple-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', icon: 'text-indigo-600' },
    amber:  { bg: 'bg-amber-100',  text: 'text-amber-600',  icon: 'text-amber-600' },
  };
  
  return colors[color] || colors.blue;
};

/**
 * 📝 Retorna mensagem de template baseado no tipo
 * @param {string} template - Nome do template
 * @param {string} name - Nome do cliente
 * @returns {string} Mensagem formatada
 */
export const getTemplateMessage = (template, name = '') => {
  const templates = {
    padrao:    `Olá, ${name || ''}! 😊\n\nEstou entrando em contato da Imóveis Pro. Como posso te ajudar hoje?`,
    proposta:  `Olá, ${name || ''}! 👋\n\nTenho uma proposta interessante para você. Podemos conversar?`,
    visita:    `Olá, ${name || ''}! 🏠\n\nGostaria de agendar uma visita para o imóvel que você demonstrou interesse.`,
    lembrete:  `Olá, ${name || ''}! ⏰\n\nPassando para lembrar da nossa conversa sobre imóveis.`
  };
  
  return templates[template] || templates.padrao;
};

/**
 * 🎯 Retorna opções de filtro com labels
 * @returns {Array} Opções de filtro
 */
export const getFilterOptions = () => [
  { value: 'todos',              label: 'Todos os clientes',          icon: 'Users',        mobileLabel: 'Todos' },
  { value: 'comWhatsApp',        label: 'Com WhatsApp',              icon: 'Phone',        mobileLabel: 'WhatsApp' },
  { value: 'semWhatsApp',        label: 'Sem WhatsApp',              icon: 'X',            mobileLabel: 'Sem WA' },
  { value: 'contactadosHoje',    label: 'Contactados hoje',          icon: 'Clock',        mobileLabel: 'Hoje' },
  { value: 'contactadosSemana',  label: 'Contactados esta semana',   icon: 'Calendar',     mobileLabel: 'Semana' },
  { value: 'naoContactados',     label: 'Não contactados (7+ dias)', icon: 'AlertCircle',  mobileLabel: 'Inativos' },
  { value: 'Comprador',          label: 'Compradores',               icon: 'User',         mobileLabel: 'Comp.' },
  { value: 'Vendedor',           label: 'Vendedores',                icon: 'User',         mobileLabel: 'Vend.' },
  { value: 'Locatário',          label: 'Locatários',                icon: 'User',         mobileLabel: 'Loc.' },
  { value: 'Investidor',         label: 'Investidores',              icon: 'TrendingUp',   mobileLabel: 'Inv.' }
];

// ============================================================================
// 🔥 V2: NUEVAS FUNCIONES — MessageHistoryModal Ultra Premium
// ============================================================================

/**
 * ✂️ Trunca texto com reticências preservando palavras
 * @param {string} text - Texto original
 * @param {number} maxLength - Comprimento máximo (default 120)
 * @returns {string} Texto truncado
 */
export const truncateText = (text = '', maxLength = 120) => {
  if (typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;
  
  // Corta no espaço mais próximo para não quebrar palavras
  const truncated = text.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > maxLength * 0.7 
    ? truncated.slice(0, lastSpace) + '...' 
    : truncated + '...';
};

/**
 * 📅 Formata data/hora completa para tooltips e detalhes
 * @param {number} timestamp - Timestamp em milissegundos
 * @returns {string} Data completa formatada (ex: "15 de janeiro de 2026 às 14:30")
 */
export const formatFullDateTime = (timestamp) => {
  if (!timestamp) return 'Data desconhecida';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return 'Data inválida';
  }
};

/**
 * 🏷️ Gera ID único para mensagem reenviada
 * @param {string} originalMessageId - ID da mensagem original
 * @returns {string} Novo ID único para o reenvio
 */
export const generateResendId = (originalMessageId = '') => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const base = originalMessageId 
    ? originalMessageId.replace(/[^a-zA-Z0-9]/g, '').slice(-6) 
    : 'resend';
  return `${base}_${timestamp}${random}`;
};

/**
 * 🔒 Verifica se uma mensagem pode ser editada
 * Regras:
 * - Apenas mensagens enviadas por você (outgoing)
 * - Apenas mensagens com menos de 1 hora
 * - Status não pode ser 'failed'
 * @param {Object} message - Objeto da mensagem
 * @returns {boolean} True se editável
 */
export const isMessageEditable = (message) => {
  if (!message || typeof message !== 'object') return false;
  
  // Só mensagens outgoing
  if (message.direction !== 'outgoing') return false;
  
  // Não editar mensagens com falha
  if (message.status === 'failed') return false;
  
  // Janela de edição: 1 hora (3600000 ms)
  const now = Date.now();
  const createdAt = message.createdAt || 0;
  const oneHour = 60 * 60 * 1000;
  
  return (now - createdAt) < oneHour;
};

/**
 * 🧹 Sanitiza mensagem antes de editar
 * Remove espaços extras, normaliza quebras de linha
 * @param {string} text - Texto a ser sanitizado
 * @returns {string} Texto limpo
 */
export const sanitizeMessageForEdit = (text = '') => {
  if (typeof text !== 'string') return '';
  
  return text
    .trim()
    .replace(/\r\n/g, '\n')      // Normaliza Windows
    .replace(/\r/g, '\n')        // Normaliza Mac antigo
    .replace(/\n{3,}/g, '\n\n')  // Máximo 2 quebras consecutivas
    .replace(/[ \t]{2,}/g, ' '); // Remove espaços múltiplos
};

/**
 * 🎯 Retorna permissões de ações para uma mensagem
 * @param {Object} message - Objeto da mensagem
 * @returns {Object} Objeto com permissões booleanas
 */
export const getMessageActionPermissions = (message) => {
  if (!message || typeof message !== 'object') {
    return {
      canView: false,
      canEdit: false,
      canResend: false,
      canDelete: true,
      canCopy: false,
    };
  }

  const isOutgoing = message.direction === 'outgoing';
  const isFailed = message.status === 'failed';
  
  return {
    canView: true,                          // Sempre pode ver detalhes
    canEdit: isOutgoing && !isFailed && isMessageEditable(message),
    canResend: isOutgoing && !isFailed,     // Pode reenviar mensagens próprias
    canDelete: true,                        // Sempre pode deletar
    canCopy: !!message.message,            // Pode copiar se tiver conteúdo
  };
};

/**
 * 📋 Extrai texto plano de uma mensagem para clipboard
 * @param {Object} message - Objeto da mensagem
 * @returns {string} Texto pronto para copiar
 */
export const getMessageClipboardText = (message) => {
  if (!message?.message) return '';
  
  const timestamp = formatFullDateTime(message.createdAt);
  const direction = message.direction === 'outgoing' ? '📤 Enviado' : '📥 Recebido';
  
  return `${direction} em ${timestamp}:\n${message.message}`;
};

/**
 * 🧵 Agrupa mensagens por data (hoje, ontem, esta semana, etc.)
 * @param {Array} messages - Lista de mensagens ordenadas
 * @returns {Array} Array de grupos { label, messages }
 */
export const groupMessagesByDate = (messages = []) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const oneWeekAgo = today - 7 * 86400000;
  
  const groups = [];
  let currentGroup = null;
  
  messages.forEach(msg => {
    const msgDate = new Date(msg.createdAt || 0);
    const msgDay = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate()).getTime();
    
    let label;
    if (msgDay === today) {
      label = 'Hoje';
    } else if (msgDay === yesterday) {
      label = 'Ontem';
    } else if (msgDay > oneWeekAgo) {
      label = msgDate.toLocaleDateString('pt-BR', { weekday: 'long' });
    } else {
      label = msgDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    }
    
    if (!currentGroup || currentGroup.label !== label) {
      currentGroup = { label, messages: [] };
      groups.push(currentGroup);
    }
    
    currentGroup.messages.push(msg);
  });
  
  return groups;
};

// ============================================================================
// 🎯 EXPORTAÇÃO DE TODAS AS FUNÇÕES (default export para compatibilidade)
// ============================================================================
export default {
  // Existentes (Fase 1)
  normalizePhoneBR,
  formatPhoneDisplay,
  getInitials,
  getTimeAgo,
  getTemperatureColor,
  getStatusColor,
  isValidPhone,
  filterClientsByRelevance,
  formatDateTime,
  getMessageStatusInfo,
  calculateAdvancedStats,
  getStatsCardColors,
  getTemplateMessage,
  getFilterOptions,
  // Nuevas (Fase 2)
  truncateText,
  formatFullDateTime,
  generateResendId,
  isMessageEditable,
  sanitizeMessageForEdit,
  getMessageActionPermissions,
  getMessageClipboardText,
  groupMessagesByDate,
};